import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { RequestContext } from '../../../../context/RequestContext'
import { GtagContext } from '../../../../pages/_app'
import { getServiceCategorySettingsByCountryAndRequestType } from '../../../../api/request-category/request-category-api'
import { handymanCostCalculator } from '../../../../utils/handyman-cost-calculator'
import { to12HourFormat } from '../../../../utils/time-converter'
import { setDateTimeCriteria } from '../../../../utils/service-date'
import { SystemError } from '../../../error/SystemError'
import { LoadingCover } from '../../../commons/LoadingCover'
import { motion } from 'framer-motion'
import { HelpCircle, AlertTriangle, CheckCircle, DollarSign } from 'lucide-react'
import { GtagLoader, DateTimeInput, TimeSlotInput, ServiceSummaryCard, NavigationButtons } from '../../../commons/BookingFormComponents'

// Animation variants - simplified to prevent layout shifts
const containerVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            staggerChildren: 0.03,
            delayChildren: 0.05
        }
    }
};

const itemVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            duration: 0.15
        }
    }
};

// Price Warning Badge Component
const PriceWarningBadge = ({ rateAggregatorFactor }) => {
    const warningMessages = {
        late: "Selecting a time during usual business hours may be cheaper",
        emergency: "For a better price, try a weekday at least 2 days in advance",
        urgent: "High demand! A further date may be cheaper",
        sunday: "Next Sunday has high demand! Try a weekday for better price",
        weekend: "Try a weekday for better price",
        ph: "Pricing may be higher on public holidays",
        cl: "We are not operating this day",
        night: "Try booking for slots after 9am for better pricing",
        "early-morning": "Try booking for slots after 9am for better pricing"
    };

    if (!rateAggregatorFactor || !warningMessages[rateAggregatorFactor]) {
        return (
            <motion.div 
                variants={itemVariants}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-xs font-medium"
            >
                <CheckCircle className="w-3.5 h-3.5" />
                Cheapest in your locality!
            </motion.div>
        );
    }

    return (
        <motion.div 
            variants={itemVariants}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                rateAggregatorFactor === 'cl' 
                    ? 'bg-red-50 text-red-700' 
                    : 'bg-amber-50 text-amber-700'
            }`}
        >
            <AlertTriangle className="w-3.5 h-3.5" />
            {warningMessages[rateAggregatorFactor]}
        </motion.div>
    );
};

// Cost Display Card
const CostDisplayCard = ({ servicingCost, serviceType, countryCurrCd, onHelpClick }) => {
    if (servicingCost <= 0) return null;

    return (
        <motion.div 
            variants={itemVariants}
            className="bg-linear-to-br from-orange-50 to-amber-50 rounded-xl p-2 border border-orange-100"
        >
            <div className="text-center space-y-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                    {serviceType === "Visit & Inspection" ? "Visit Cost" : "Estimated Cost"}
                </div>
                <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl font-bold text-gray-800">{countryCurrCd} {servicingCost}</span>
                    <button 
                        type="button" 
                        onClick={onHelpClick}
                        className="p-1 text-orange-500 hover:text-orange-600 hover:bg-orange-100 rounded-full transition-colors"
                    >
                        <HelpCircle className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export const HandymanDateTime = (props) => {
    
    const goBack = props.goBack
    const goToSection4 = props.goToSection4    
    const countryName = props.countryName
    const countryCurrCd = props.countryCurrCd

    const serviceHours = 2
    const [ minAllowed, setMinAllowed ] = useState("09:00")
    const [ maxAllowed, setMaxAllowed ] = useState("19:00")
    const [ minDate, setMinDate ] = useState("")
    const [ maxDate, setMaxDate ] = useState("")  
    const [ serviceType, setServiceType ] = useState("")
    const [ bookingFee, setBookingFee ] = useState("")
    const isInitialLoaded = useRef(false)

    const [ isLoading, setIsLoading ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false)
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}) 
    const [ displayServiceDate, setDisplayServiceDate ] = useState("")
    const { isGtagLoaded } = useContext(GtagContext)

    const currentDate = new Date().toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10)

    const { 
        requestCriteria, 
        requestType, 
        serviceDate, 
        setServiceDate, 
        startTime,
        setStartTime,
        endTime,
        setEndTime, 
        setServiceCost,
        serviceSummary, 
        setServiceSummary,
        setTotalDiscountPercent,
        setOriginalCost
    } = useContext(RequestContext);

    //HANDYMAN COST CALCULATION
    const [ servicingCost, setServicingCost ] = useState(0)
    const [ rateAggregatorFactor, setRateAggregatorFactor ] = useState("")
    //HANDYMAN COST CALCULATION

    const handleServiceDateChange = (e) => {
        setServiceDate(e.target.value)
        setDisplayServiceDate(new Date(e.target.value).toString().substring(0,16))
        if(e.target.value === currentDate){          
            const min = new Date().getHours() + 1
            setMinAllowed(min + ":00")  
            const max = min + 2 
            setMaxAllowed(max + ":00")         
        } else {
            setMinAllowed("09:00")    
            setMaxAllowed("19:00")
        }
    }

    const handleStartTimeChange = (e) => {
        setStartTime(e.target.value)
    }

    useEffect(() => {
        if(!startTime || !serviceHours) return

        const [minHour, minMinutes] = startTime.split(":").map(Number);
        const serviceDuration = parseFloat(serviceHours);
    
        let hour = minHour + Math.floor(serviceDuration);
        let minutes = minMinutes + (serviceDuration % 1) * 60;
    
        if (minutes >= 60) {
            minutes -= 60;
            hour += 1;
        }
    
        if (hour >= 24) {
            hour -= 24;
        }
    
        const formattedHour = hour.toString().padStart(2, "0");
        const formattedMinutes = minutes.toString().padStart(2, "0");

        setEndTime(`${formattedHour}:${formattedMinutes}`);

    }, [startTime, setEndTime, serviceHours])

    const submit = (e) => {
        e.preventDefault()

        setDateTimeCriteria(requestCriteria,
            {
                'criteria': "Requested service date - " + displayServiceDate,
                [99]: serviceDate
            },
            {
                'criteria': "Arrival time slot - " + to12HourFormat(startTime) + " to " + to12HourFormat(endTime),
                [100]: to12HourFormat(startTime) + " to " + to12HourFormat(endTime)
            }
        )

        if(servicingCost > 0){
            setServiceCost(servicingCost)
        }   

        goToSection4()
    }
    
    const handymanCostCalculatorApi = handymanCostCalculator(requestCriteria, serviceDate, startTime, endTime, setServicingCost, setRateAggregatorFactor, setServiceSummary
        , setServiceType
        , setTotalDiscountPercent
        , setOriginalCost
        , setBookingFee
        , setIsLoading
        , setErrorContent
        , setIsSystemError
    )

    const refresh = useCallback(() => {
        if(isInitialLoaded.current) return
        handymanCostCalculatorApi()
    }, [isInitialLoaded, handymanCostCalculatorApi])

    useEffect(() => {
        if(!requestCriteria) return
        if(!isGtagLoaded) return

        if(!isInitialLoaded.current){
            isInitialLoaded.current = true
            handymanCostCalculatorApi()
        } else {
            handymanCostCalculatorApi()
        }
    }, [requestCriteria, isGtagLoaded, handymanCostCalculatorApi])

    useEffect(() => {
        if(!countryName || !requestType) return

        const getServiceCategorySettings = async() => {
            try {
                const serviceSetting = await getServiceCategorySettingsByCountryAndRequestType(countryName, requestType)
                
                const minD = new Date()
                
                if(minD.getHours() > 21) {
                    minD.setDate(minD.getDate() + 1)
                }

                if(serviceSetting.nextSlotDate){
                    minD.setDate(new Date(serviceSetting.nextSlotDate))
                }

                setMinDate(minD.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))
                
                const sDate = new Date()                 
                sDate.setDate(sDate.getDate() + 2)
                if(minD > sDate){
                    sDate.setDate(minD)
                }

                if(sDate.getDay() === 0){
                    sDate.setDate(sDate.getDate() + 1)
                }

                if(sDate.getDay() === 6){
                    sDate.setDate(sDate.getDate() + 2)
                }
                
                setServiceDate(sDate.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))
                setDisplayServiceDate(sDate.toString().substring(0,16))

                const maxD = new Date()
                maxD.setDate(maxD.getDate() + 182)
                setMaxDate(maxD.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))

                setStartTime("13:00")

            } catch (error) {
                console.log(error)
            }
        }
        
        getServiceCategorySettings();

    }, [setServiceDate, setStartTime, countryName, requestType])

    const showVisitCostHelp = () => {
        if(serviceType === "Visit & Inspection"){
            setErrorContent({'header': "Visit & Inspection", isInfo: true,
            'body': `This is the visit and inspection fee only. The exact pricing can be stated only after physical checking at your premises.`});
        } else {
            setErrorContent({'header': "Estimated Cost", isInfo: true,
                'body': `This is an estimated pricing based on your initial requirements. The exact pricing can be stated only after physical checking at your premises. If you do not wish to proceed with the service after inspection or when you cancel the service with less than 24hrs remaining from the service date a visit fee of S$${bookingFee} will be charged. Please refer to cancellation policy for details.`});
        }

        setIsSystemError(true);
    }

    return (
        <>
            <form className="max-w-lg mx-auto px-3 py-4" onSubmit={(e) => submit(e)}>
                {/* Loading State */}                
                <div className="min-h-35 space-y-2">
                    <GtagLoader isLoading={!isGtagLoaded} />                    
                    {isGtagLoaded && (
                        <motion.div 
                            className="space-y-2"
                            variants={containerVariants}
                            initial="initial"
                            animate="animate"
                        >
                            {/* Cost Display */}
                            <CostDisplayCard 
                                servicingCost={servicingCost}
                                serviceType={serviceType}
                                countryCurrCd={countryCurrCd}
                                onHelpClick={showVisitCostHelp}
                            />
                            
                            {/* Price Warning */}
                            <motion.div variants={itemVariants} className="text-center">
                                <PriceWarningBadge rateAggregatorFactor={rateAggregatorFactor} />
                            </motion.div>
                        </motion.div>
                    )}
                </div>
                {/* Date & Time Selection */}
                <motion.div 
                    variants={itemVariants}
                    className="grid sm:grid-cols-2 gap-4 mb-4"
                >
                    {/* Service Date */}
                    <DateTimeInput
                        type="date"
                        value={serviceDate}
                        min={minDate}
                        max={maxDate}
                        onChange={handleServiceDateChange}
                        label="Service Date"
                    />
                    
                    {/* Arrival Time */}
                    <TimeSlotInput
                        startTime={startTime}
                        endTime={endTime}
                        onStartTimeChange={handleStartTimeChange}
                        minAllowed={minAllowed}
                        maxAllowed={maxAllowed}
                        serviceHours={serviceHours}
                        variant="arrival"
                    />
                </motion.div>
                
                {/* Service Summary */}
                <motion.div variants={itemVariants} className='min-h-24'>
                    <ServiceSummaryCard summary={serviceSummary} />
                </motion.div>
                
                {/* Navigation Buttons */}
                <NavigationButtons
                    onBack={() => goBack()}
                    showNext={rateAggregatorFactor !== "cl"}
                />
            </form>
            
            {/* Scripts removed - now loaded globally in _app.js */}
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
            {isLoading && <LoadingCover/>}
        </>
    )
}
